import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiBell, FiSend, FiFilter, FiSmartphone, FiUsers, FiCalendar } from 'react-icons/fi';

const authH = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const card = { background:'#fff', borderRadius:10, border:'1px solid #e5e7eb', padding:20, boxShadow:'0 1px 3px rgba(0,0,0,0.04)' };
const cardTitle = { fontSize:14, fontWeight:700, color:'#111827', marginBottom:14, display:'flex', alignItems:'center', gap:6 };
const lbl = { display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:5 };
const inp = { width:'100%', padding:'9px 11px', borderRadius:6, border:'1px solid #d1d5db', fontSize:13, color:'#111827', background:'#fff', boxSizing:'border-box', outline:'none' };
const pageBtn = { padding:'5px 12px', borderRadius:6, border:'1px solid #e5e7eb', background:'#fff', cursor:'pointer', fontSize:12, color:'#374151' };


export default function AdminLeadPush({ token }) {
  const [campaignFilter, setCampaignFilter] = useState('aca');
  const [agentStatusFilter, setAgentStatusFilter] = useState('active');
  const [title, setTitle]       = useState('');
  const [body, setBody]         = useState('');
  const [platform, setPlatform] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [agents, setAgents]     = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentSearch, setAgentSearch]     = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [leads, setLeads]       = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsPage, setLeadsPage]       = useState(1);
  const [leadsTotal, setLeadsTotal]     = useState(0);
  const [sending, setSending]       = useState(false);
  const [result, setResult]         = useState(null);
  const [selectedEmails, setSelectedEmails] = useState(new Set()); // manually selected rows
  const LIMIT = 50;

  const toggleSelect = (email) => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (leads.every(l => selectedEmails.has(l.leadEmail))) {
      // all selected → deselect all on this page
      setSelectedEmails(prev => {
        const next = new Set(prev);
        leads.forEach(l => next.delete(l.leadEmail));
        return next;
      });
    } else {
      // select all on this page
      setSelectedEmails(prev => {
        const next = new Set(prev);
        leads.forEach(l => next.add(l.leadEmail));
        return next;
      });
    }
  };
  const clearSelection = () => setSelectedEmails(new Set());
  // How many will actually receive: if any manually selected, use that count; else use filter total
  const recipientCount = selectedEmails.size > 0 ? selectedEmails.size : leadsTotal;

  useEffect(() => {
    setAgentsLoading(true);
    setSelectedAgent(null);
    const params = new URLSearchParams();
    if (campaignFilter !== 'all') params.set('campaignId', campaignFilter);
    if (agentStatusFilter !== 'all') params.set('accountStatus', agentStatusFilter);
    axios.get(`/api/admin/agents/summary?${params}`, authH(token))
      .then(r => {
        const list = [];
        for (const a of (r.data || [])) {
          if (a.agentType === 'group' && a.groupMembers && a.groupMembers.length) {
            for (const m of a.groupMembers) {
              if (m.email) list.push({ email: m.email, name: m.name || m.email, agency: a.ghlLocationName || a.name || '' });
            }
          } else if (a.email) {
            list.push({ email: a.email, name: a.name || a.email, agency: a.agency || '' });
          }
        }
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setAgents(list);
      }).catch(() => {}).finally(() => setAgentsLoading(false));
  }, [token, campaignFilter, agentStatusFilter]);

  const loadLeads = useCallback(async (pg = 1) => {
    setLeadsLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: LIMIT });
      if (campaignFilter !== 'all') params.set('campaign', campaignFilter);
      if (platform !== 'all') params.set('platform', platform);
      if (selectedAgent) params.set('search', selectedAgent.email);
      const r = await axios.get(`/api/lead-portal/admin/app-stats?${params}`, authH(token));
      let rows = r.data.leads || [];
      if (dateFrom) rows = rows.filter(l => l.appFirstSeen && new Date(l.appFirstSeen) >= new Date(dateFrom));
      if (dateTo)   rows = rows.filter(l => l.appFirstSeen && new Date(l.appFirstSeen) <= new Date(dateTo + 'T23:59:59'));
      setLeads(rows);
      setLeadsTotal(dateFrom || dateTo ? rows.length : (r.data.total || 0));
      setLeadsPage(pg);
    } catch (e) { console.error(e); }
    finally { setLeadsLoading(false); }
  }, [token, campaignFilter, platform, selectedAgent, dateFrom, dateTo]);

  useEffect(() => { loadLeads(1); }, [loadLeads]);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) { toast.error('Title and message are required'); return; }
    if (leadsTotal === 0) { toast.error('No matching recipients'); return; }
    const sendCount = selectedEmails.size > 0 ? selectedEmails.size : leadsTotal;
    if (!window.confirm('Send push to ' + sendCount + ' leads?')) return;
    setSending(true); setResult(null);
    try {
      const r = await axios.post('/api/lead-portal/admin/push-leads', {
        title: title.trim(), body: body.trim(), platform,
        agentEmail: selectedAgent ? selectedAgent.email : null,
        dateFrom: dateFrom || null, dateTo: dateTo || null,
        // If specific rows selected, send only to those emails
        targetEmails: selectedEmails.size > 0 ? [...selectedEmails] : null,
      }, authH(token));
      setResult(r.data);
      toast.success('Sent to ' + r.data.sent + ' leads!');
    } catch (e) { toast.error((e.response && e.response.data && e.response.data.error) || 'Send failed'); }
    finally { setSending(false); }
  };

  const filteredAgents = agents.filter(a =>
    !agentSearch ||
    (a.name || '').toLowerCase().includes(agentSearch.toLowerCase()) ||
    (a.email || '').toLowerCase().includes(agentSearch.toLowerCase()) ||
    (a.agency || '').toLowerCase().includes(agentSearch.toLowerCase())
  );

  return (
    <div style={{ padding:24, maxWidth:1200, margin:'0 auto', fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:28 }}>
        <FiBell size={24} color="#0F5298" />
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, color:'#111827', margin:0 }}>Lead Push Notifications</h1>
          <p style={{ fontSize:14, color:'#6b7280', margin:0 }}>Send push notifications to app users</p>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'380px 1fr', gap:24, alignItems:'start' }}>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          <div style={card}>
            <h2 style={cardTitle}><FiBell size={15} /> Compose</h2>
            <label style={lbl}>Title *</label>
            <input style={inp} placeholder="e.g. Action Required: Review Your Plan" value={title} onChange={e => setTitle(e.target.value)} maxLength={100} />
            <div style={{ fontSize:11, color:'#9ca3af', textAlign:'right', marginTop:2 }}>{title.length}/100</div>
            <label style={{ ...lbl, marginTop:12 }}>Message *</label>
            <textarea style={{ ...inp, height:100, resize:'vertical' }} placeholder="Your message here..." value={body} onChange={e => setBody(e.target.value)} maxLength={250} />
            <div style={{ fontSize:11, color:'#9ca3af', textAlign:'right', marginTop:2 }}>{body.length}/250</div>
          </div>

          <div style={card}>
            <h2 style={cardTitle}><FiFilter size={15} /> Audience Filters</h2>

            <label style={lbl}>Campaign</label>
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              {[['all','All'],['aca','ACA'],['medicare','Medicare']].map(([v, l]) => (
                <button key={v} onClick={() => setCampaignFilter(v)} style={{ flex:1, padding:'7px 0', borderRadius:6, border:'1px solid', borderColor:campaignFilter===v?'#0F5298':'#e5e7eb', background:campaignFilter===v?'#0F5298':'#fff', color:campaignFilter===v?'#fff':'#374151', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  {l}
                </button>
              ))}
            </div>

            <label style={lbl}>Agent Status</label>
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              {[['active','Active'],['inactive','Inactive'],['all','All']].map(([v, l]) => (
                <button key={v} onClick={() => setAgentStatusFilter(v)} style={{ flex:1, padding:'7px 0', borderRadius:6, border:'1px solid', borderColor:agentStatusFilter===v?(v==='inactive'?'#dc2626':v==='all'?'#6b7280':'#0F5298'):'#e5e7eb', background:agentStatusFilter===v?(v==='inactive'?'#dc2626':v==='all'?'#6b7280':'#0F5298'):'#fff', color:agentStatusFilter===v?'#fff':'#374151', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  {v==='active'?'🟢 ':v==='inactive'?'🔴 ':'⚪ '}{l}
                </button>
              ))}
            </div>

            <label style={lbl}>Platform</label>
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              {[['all','All Devices'],['ios','iPhone/iPad'],['android','Android']].map(([v, l]) => (
                <button key={v} onClick={() => setPlatform(v)} style={{ flex:1, padding:'7px 0', borderRadius:6, border:'1px solid', borderColor:platform===v?'#0F5298':'#e5e7eb', background:platform===v?'#0F5298':'#fff', color:platform===v?'#fff':'#374151', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  {v==='ios'?'📱 ':v==='android'?'🤖 ':'📲 '}{l}
                </button>
              ))}
            </div>
            <label style={lbl}><FiCalendar size={11} /> First Seen From</label>
            <input type="date" style={{ ...inp, marginBottom:10 }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            <label style={lbl}>First Seen To</label>
            <input type="date" style={inp} value={dateTo} onChange={e => setDateTo(e.target.value)} />
            <label style={{ ...lbl, marginTop:14 }}><FiUsers size={11} /> Filter by Agent / Member</label>
            {selectedAgent ? (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:6 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:'#1e40af' }}>{selectedAgent.name}</div>
                  <div style={{ fontSize:11, color:'#6b7280' }}>{selectedAgent.email}</div>
                </div>
                <button onClick={() => setSelectedAgent(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:20 }}>×</button>
              </div>
            ) : (
              <>
                <input style={inp} placeholder="Search agent name or email..." value={agentSearch} onChange={e => setAgentSearch(e.target.value)} />
                {agentSearch && (
                  <div style={{ maxHeight:180, overflowY:'auto', border:'1px solid #e5e7eb', borderRadius:6, marginTop:4, background:'#fff' }}>
                    {agentsLoading ? <div style={{ padding:12, fontSize:13, color:'#9ca3af' }}>Loading...</div>
                      : filteredAgents.length === 0 ? <div style={{ padding:12, fontSize:13, color:'#9ca3af' }}>No results</div>
                      : filteredAgents.slice(0, 30).map(a => (
                        <div key={a.email} onClick={() => { setSelectedAgent(a); setAgentSearch(''); }}
                          style={{ padding:'8px 12px', cursor:'pointer', borderBottom:'1px solid #f3f4f6', fontSize:13 }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                          <div style={{ fontWeight:600 }}>{a.name}</div>
                          <div style={{ fontSize:11, color:'#9ca3af' }}>{a.email}{a.agency ? ' - ' + a.agency : ''}</div>
                        </div>
                      ))
                    }
                  </div>
                )}
              </>
            )}
          </div>

          {selectedEmails.size > 0 && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', background:'#fefce8', border:'1px solid #fde68a', borderRadius:6, fontSize:13 }}>
              <span style={{ fontWeight:600, color:'#92400e' }}>✓ {selectedEmails.size} lead{selectedEmails.size !== 1 ? 's' : ''} manually selected</span>
              <button onClick={clearSelection} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:12 }}>Clear</button>
            </div>
          )}
          <button onClick={handleSend} disabled={sending || !title.trim() || !body.trim()} style={{ width:'100%', padding:14, borderRadius:8, border:'none', background:sending||!title.trim()||!body.trim()?'#9ca3af':'#0F5298', color:'#fff', fontSize:15, fontWeight:700, cursor:sending?'wait':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
            <FiSend size={16} />
            {sending ? 'Sending...' : 'Send to ' + recipientCount.toLocaleString() + ' Lead' + (recipientCount !== 1 ? 's' : '')}
          </button>

          {result && (
            <div style={{ padding:14, borderRadius:8, background:'#f0fdf4', border:'1px solid #bbf7d0' }}>
              <div style={{ fontSize:14, fontWeight:700, color:'#15803d' }}>✅ Push Sent!</div>
              <div style={{ fontSize:13, color:'#374151', marginTop:4 }}>Delivered: {result.sent} | Failed: {result.failed} | Targeted: {result.total}</div>
            </div>
          )}
        </div>


        <div style={card}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <h2 style={{ ...cardTitle, marginBottom:0 }}>
              <FiSmartphone size={15} /> Audience Preview
              <span style={{ marginLeft:8, fontSize:12, fontWeight:400, color:'#6b7280' }}>{leadsTotal.toLocaleString()} leads{selectedEmails.size > 0 ? ` · ${selectedEmails.size} selected` : ''}</span>
            </h2>
            <div style={{ display:'flex', gap:8 }}>
              {selectedEmails.size > 0 && (
                <button onClick={clearSelection} style={{ background:'none', border:'1px solid #fde68a', borderRadius:6, padding:'5px 10px', fontSize:12, cursor:'pointer', color:'#92400e', background:'#fefce8' }}>Clear Selection</button>
              )}
              <button onClick={() => loadLeads(1)} style={{ background:'none', border:'1px solid #e5e7eb', borderRadius:6, padding:'5px 10px', fontSize:12, cursor:'pointer', color:'#374151' }}>Refresh</button>
            </div>
          </div>
          <div style={{ overflowY:'auto', maxHeight:520, borderRadius:6, border:'1px solid #e5e7eb' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#f9fafb', position:'sticky', top:0, zIndex:1 }}>
                  <th style={{ padding:'10px 12px', borderBottom:'1px solid #e5e7eb', width:36 }}>
                    <input type="checkbox"
                      checked={leads.length > 0 && leads.every(l => selectedEmails.has(l.leadEmail))}
                      ref={el => { if (el) el.indeterminate = leads.some(l => selectedEmails.has(l.leadEmail)) && !leads.every(l => selectedEmails.has(l.leadEmail)); }}
                      onChange={toggleSelectAll}
                      style={{ cursor:'pointer', width:15, height:15 }}
                    />
                  </th>
                  {['Lead','Phone','Agent','Campaign','Platform','First Seen'].map(h => (
                    <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontWeight:600, color:'#374151', borderBottom:'1px solid #e5e7eb', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leadsLoading ? (
                  <tr><td colSpan={6} style={{ padding:24, textAlign:'center', color:'#9ca3af' }}>Loading...</td></tr>
                ) : leads.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding:24, textAlign:'center', color:'#9ca3af' }}>No leads match filters</td></tr>
                ) : leads.map((l, i) => (
                  <tr key={l.leadEmail} onClick={() => toggleSelect(l.leadEmail)} style={{ background:selectedEmails.has(l.leadEmail)?'#eff6ff':i%2===0?'#fff':'#fafafa', borderBottom:'1px solid #f3f4f6', cursor:'pointer' }}>
                    <td style={{ padding:'9px 12px' }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedEmails.has(l.leadEmail)} onChange={() => toggleSelect(l.leadEmail)} style={{ cursor:'pointer', width:15, height:15 }} />
                    </td>
                    <td style={{ padding:'9px 12px' }}>
                      <div style={{ fontWeight:600, color:'#111827' }}>{l.leadName || l.leadEmail}</div>
                      <div style={{ fontSize:11, color:'#9ca3af' }}>{l.leadEmail}</div>
                    </td>
                    <td style={{ padding:'9px 12px', color:'#374151' }}>{l.leadPhone || '-'}</td>
                    <td style={{ padding:'9px 12px' }}>
                      {l.agents && l.agents.length ? l.agents.map(a => (
                        <div key={a.email} style={{ fontSize:12 }}>
                          <div style={{ fontWeight:600, color:'#111827' }}>{a.name || a.email}</div>
                          {a.name && <div style={{ fontSize:11, color:'#9ca3af' }}>{a.email}</div>}
                        </div>
                      )) : <span style={{ color:'#9ca3af', fontSize:12 }}>-</span>}
                    </td>
                    <td style={{ padding:'9px 12px' }}>
                      {(l.campaigns || []).map(c => (
                        <span key={c} style={{ padding:'2px 7px', background:'#eff6ff', color:'#1e40af', borderRadius:10, fontSize:11, fontWeight:600 }}>{c.toUpperCase()}</span>
                      ))}
                    </td>
                    <td style={{ padding:'9px 12px', textAlign:'center' }}>
                      {l.appPlatform === 'ios' ? '📱 iOS' : l.appPlatform === 'android' ? '🤖 Android' : '-'}
                    </td>
                    <td style={{ padding:'9px 12px', color:'#374151', whiteSpace:'nowrap' }}>
                      {l.appFirstSeen ? new Date(l.appFirstSeen).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {leadsTotal > LIMIT && (
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12, fontSize:13, color:'#6b7280' }}>
              <span>Page {leadsPage} of {Math.ceil(leadsTotal / LIMIT)}</span>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => loadLeads(leadsPage - 1)} disabled={leadsPage <= 1} style={pageBtn}>← Prev</button>
                <button onClick={() => loadLeads(leadsPage + 1)} disabled={leadsPage >= Math.ceil(leadsTotal / LIMIT)} style={pageBtn}>Next →</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

